/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      // Auto-select available variant if current selection is unavailable
      if (evt?.target && !this.preSelection && (!this.variant || !this.variant.available)) {
        const changedSelector = evt.target.closest('.option-selector');
        if (changedSelector) {
          this.selectorArray = this.selectorArray || Array.from(this.optionSelectors);
          const changedIndex = this.selectorArray.indexOf(changedSelector);
          const availableVariant = this.findAvailableVariant(changedIndex);
          if (availableVariant) {
            this.selectVariantSilently(availableVariant);
            this.selectedOptions = this.getSelectedOptions();
            this.variant = this.getSelectedVariant();
          }
        }
      }

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {

        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);
      if(!available){
        el.setAttribute('disabled', true);
      } else {
        el.removeAttribute('disabled');
      }

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) {
            variant = this.data.product.variants[i];
          }
        }
      }

      const priceCurrentEl = this.price.querySelector('.price__current');
      const priceWasEl = this.price.querySelector('.price__was');
      const unitPriceEl = this.price.querySelector('.unit-price');

      if (!variant || !variant.available) {
        if (priceCurrentEl) priceCurrentEl.innerHTML = theme.strings.noStock || 'Sold out';
        if (priceWasEl) priceWasEl.innerHTML = '';
        if (unitPriceEl) unitPriceEl.hidden = true;

        this.price.classList.remove('price--on-sale');
        this.price.classList.add('price--sold-out');

        return;
      }

      // Variant available: show price normally
      if (priceCurrentEl) priceCurrentEl.innerHTML = this.data.formatted[variant.id].price;
      if (priceWasEl) priceWasEl.innerHTML = this.data.formatted[variant.id].compareAtPrice || '';

      if (variant.unit_price_measurement) {
        const valueEl = this.price.querySelector('.unit-price__price');
        const unitEl = this.price.querySelector('.unit-price__unit');
        const value = variant.unit_price_measurement.reference_value;
        const unit = variant.unit_price_measurement.reference_unit;

        valueEl.innerHTML = this.data.formatted[variant.id].unitPrice;
        unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
      }

      unitPriceEl.hidden = !variant.unit_price_measurement;

      this.price.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
      this.price.classList.remove('price--sold-out');

      this.price.querySelector('.price__default').hidden = false;
      this.price.querySelector('.price__no-variant').hidden = true;

      const from = this.price.querySelector('.price__from');
      if (from) from.hidden = true;
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
          selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }

    /**
     * Finds an available variant by trying the next options in sequential order.
     * Prioritizes the next available option in the selector order.
     * @param {number} changedIndex - Index of the selector that was just changed.
     * @returns {?object} Available variant object, or null if none found.
     */
    findAvailableVariant(changedIndex) {
      const changedOptionValue = this.selectedOptions[changedIndex];

      // Try to find an available variant with the changed option first
      for (const variant of this.data.product.variants) {
        if (!variant.available) continue;
        if (variant.options[changedIndex] !== changedOptionValue) continue;

        const allMatch = variant.options.every((opt, i) =>
          i === changedIndex || opt === this.selectedOptions[i]
        );

        if (allMatch) {
          return variant;
        }
      }

      // No perfect match - find next available option for other selectors
      // Get all option values in order for each selector
      const selectorOptions = [];
      this.optionSelectors.forEach((selector, selectorIndex) => {
        const options = [];
        if (selector.dataset.selectorType === 'dropdown') {
          selector.querySelectorAll('.js-option').forEach(opt => {
            const value = opt.dataset.value;
            if (value) options.push(value);
          });
        } else {
          selector.querySelectorAll('.js-option').forEach(opt => {
            if (opt.value) options.push(opt.value);
          });
        }
        selectorOptions[selectorIndex] = options;
      });

      // Try to find next available variant by checking options in order
      for (let selectorIndex = 0; selectorIndex < this.optionSelectors.length; selectorIndex++) {
        if (selectorIndex === changedIndex) continue;

        const currentValue = this.selectedOptions[selectorIndex];
        const availableOptions = selectorOptions[selectorIndex];
        const currentIdx = availableOptions.indexOf(currentValue);

        // Try options after the current one
        for (let i = currentIdx + 1; i < availableOptions.length; i++) {
          const testOptions = [...this.selectedOptions];
          testOptions[selectorIndex] = availableOptions[i];

          const variant = this.data.product.variants.find(v =>
            v.available && v.options.every((opt, idx) => opt === testOptions[idx])
          );

          if (variant) {
            return variant;
          }
        }

        // Try options before the current one (wrap around)
        for (let i = 0; i < currentIdx; i++) {
          const testOptions = [...this.selectedOptions];
          testOptions[selectorIndex] = availableOptions[i];

          const variant = this.data.product.variants.find(v =>
            v.available && v.options.every((opt, idx) => opt === testOptions[idx])
          );

          if (variant) {
            return variant;
          }
        }
      }

      return this.data.product.variants.find(v => v.available) || null;
    }

    /**
     * Programmatically selects a variant by updating the selectors without triggering events.
     * @param {object} variant - Variant to select.
     */
    selectVariantSilently(variant) {
      this.optionSelectors.forEach((selector, index) => {
        const optionValue = variant.options[index];
        const isDropdown = selector.dataset.selectorType === 'dropdown';

        if (isDropdown) {
          const customSelect = selector.querySelector('custom-select');
          if (!customSelect) return;

          const options = selector.querySelectorAll('.js-option');
          const optionEl = Array.from(options).find(opt => opt.dataset.value === optionValue);

          if (!optionEl || optionEl.getAttribute('aria-selected') === 'true') return;

          const btn = customSelect.querySelector('.custom-select__btn');
          const listbox = customSelect.querySelector('.custom-select__listbox');
          if (!btn || !listbox) return;

          // Update button text
          const btnText = btn.querySelector('span') || btn;
          btnText.textContent = optionEl.firstElementChild?.textContent || optionEl.textContent.trim();

          // Update aria attributes
          options.forEach(opt => opt.setAttribute('aria-selected', 'false'));
          optionEl.setAttribute('aria-selected', 'true');
          listbox.setAttribute('aria-activedescendant', optionEl.id);

          // Update native select if exists
          const nativeSelect = document.getElementById(`${customSelect.id}-native`);
          if (nativeSelect) {
            nativeSelect.value = optionEl.dataset.value;
          }

          // Update swatch if applicable
          if (optionEl.dataset.swatch) {
            btn.dataset.swatch = optionEl.dataset.swatch;
          }
        } else {
          // Radio button selectors
          const options = selector.querySelectorAll('.js-option');
          const optionEl = Array.from(options).find(opt => opt.value === optionValue);

          if (!optionEl || optionEl.checked) return;

          optionEl.checked = true;

          // Update color text label if exists
          const colorText = selector.querySelector('.js-color-text');
          if (colorText) {
            const valueEl = optionEl.nextElementSibling?.querySelector('.js-value');
            if (valueEl) {
              colorText.textContent = valueEl.textContent;
            }
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
